import { createContext, useContext, useMemo } from "react";

import type { DashboardBranding } from "../theme/branding-types";

const DashboardBrandingContext = createContext<DashboardBranding | null>(null);

/**
 * Makes the current dashboard's branding available to components rendered inside it --
 * chiefly the logo, which has no other way to know which dashboard it sits in.
 *
 * Context rather than `PLUGIN_LOGO_ICON_COMPONENTS`: that slot is a module-level array, so
 * filling it swaps the logo for the whole app. Per-dashboard branding has to stay scoped
 * to one subtree, and context is the mechanism React already provides for that.
 */
export function DashboardBrandingContextProvider({
  branding,
  children,
}: {
  branding?: DashboardBranding | null;
  children: React.ReactNode;
}) {
  const value = useMemo(() => branding ?? null, [branding]);

  return (
    <DashboardBrandingContext.Provider value={value}>
      {children}
    </DashboardBrandingContext.Provider>
  );
}

/** Returns the enclosing dashboard's branding, or `null` outside a branded dashboard. */
export function useDashboardBrandingContext(): DashboardBranding | null {
  return useContext(DashboardBrandingContext);
}
